import React from 'react'
import { Box, Typography } from '@mui/material'
// The shared empty-value glyph, so this app and the platform can never disagree
// on what an absent value looks like (see the constant's docstring).
import { EMPTY_VALUE } from '@repo/ui'

export interface UserIdentitySource {
  first_name?: string | null
  last_name?: string | null
  username?: string | null
  email?: string | null
}

/**
 * VERA-304: the ONE place that decides how a person is labelled.
 *
 * The component was already shared; the RESOLVER was not, so the two call sites
 * drifted into showing different things for the same person:
 *   - RecipientSearchPicker  `getName(c) || c.email`     -> the EMAIL
 *   - GiftCertificate        `getName(u) || u.username`  -> the username, while
 *     `subtext` independently rendered `@username`, printing it TWICE
 *
 * Order, and why:
 *  1. Real name — what a person recognises.
 *  2. `@handle` — the Venmo/PayPal model. Note this becomes the PRIMARY line and
 *     the subtext is dropped, because "spencersedb" over "@spencersedb" is the
 *     same string twice.
 *  3. Email — ONLY as the last resort, which in practice is the invite-by-email
 *     case: someone typed an address for a person with no account, so there is
 *     no name or handle to show and the address is one they just typed
 *     themselves.
 *
 * Never render the email for a person who HAS an account. Echoing back an
 * address that was used as a lookup key confirms "this account exists" to
 * anyone who types addresses into the search — an enumeration oracle. The
 * handle is the safe public identifier.
 */
export const resolveUserIdentity = (
  user?: UserIdentitySource | null,
): { name: string; subtext: string | null } => {
  if (!user) return { name: EMPTY_VALUE, subtext: null }

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
  const handle = user.username ? `@${user.username}` : null

  if (fullName) return { name: fullName, subtext: handle }
  if (handle) return { name: handle, subtext: null }
  return { name: user.email || EMPTY_VALUE, subtext: null }
}

export interface UserPreviewProps {
  /**
   * Preferred: pass the person and let the shared resolver label them, so every
   * surface agrees. `name`/`subtext` below stay for callers rendering something
   * that isn't a user record.
   */
  user?: UserIdentitySource | null
  /** Primary line — the person's display name. Overrides `user` when both are set. */
  name?: string | null
  /**
   * Secondary line only — VERA-304 verify-walkthrough feedback: never render
   * email or phone here, they're sensitive. Callers pass e.g. a `@username`.
   */
  subtext?: string | null
  avatarUrl?: string | null
  size?: number
}

/**
 * Shared avatar + name + subtext row (VERA-304, second hand-roll of this
 * pattern in apps/app — RecipientSearchPicker and ProvenanceOwnerSearchPicker
 * both rendered their own avatar-initials-fallback + name/subtext block
 * inline; see working-conventions §36, "extract on the second hand-roll").
 * Presentational only — callers own the row wrapper (padding, hover,
 * onClick, checkbox, border).
 */
export const UserPreview: React.FC<UserPreviewProps> = ({
  user,
  name: nameProp,
  subtext: subtextProp,
  avatarUrl,
  size = 40,
}) => {
  const resolved = resolveUserIdentity(user)
  const name = nameProp ?? (user ? resolved.name : undefined)
  const subtext = subtextProp ?? (user ? resolved.subtext : undefined)

  // Strip a leading '@' so a handle-only person initials as "S", not "@".
  const initial = (name || subtext || '?').trim().replace(/^@/, '').charAt(0).toUpperCase() || '?'

  return (
    <>
      <Box
        sx={theme => ({
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: theme.palette.grey[200],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0,
        })}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={name || subtext || ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {initial}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* VERA-304: 16px / weight 500, matching the product title in `ProductCard`
            and every section heading in the Gift sheet — these two rows sit directly
            above one another, so the person's name must not read smaller than the
            product's. `body2` + `fontWeight="bold"` rendered small AND heavy, which is
            the worst pair: it looked like emphasised fine print rather than a name. */}
        <Typography sx={{ fontSize: 16, fontWeight: 500 }} noWrap>
          {name || EMPTY_VALUE}
        </Typography>
        {/* The handle is supporting detail, so it takes the same 14px secondary
            treatment as the product's SKU on the neighbouring card. */}
        {subtext && (
          <Typography
            sx={theme => ({ fontSize: 14, color: theme.palette.text.secondary })}
            noWrap
          >
            {subtext}
          </Typography>
        )}
      </Box>
    </>
  )
}

export default UserPreview
